import React, { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { downloadIcon } from '@progress/kendo-svg-icons';
import DlpConfigurationService, {
    DlpConfigurationModel, DlpLookupModel, DlpProjectModel, DlpProjectSelectionError, DlpQuotaProfileRowModel, DlpScopeModel
} from '../../services/DlpConfiguration/DlpConfigurationService';
import { validateDlpConfiguration } from '../../utils/dlpConfigurationValidation';
import { buildDlpConfigurationWorkbook } from '../../utils/dlpConfigurationExport';
import { ProjectSettingsPanel } from '../../components/DlpConfiguration/Project/ProjectSettingsPanel';
import { ScopesPanel } from '../../components/DlpConfiguration/Scopes/ScopesPanel';
import { ProfilesPanel } from '../../components/DlpConfiguration/QuotaProfiles/ProfilesPanel';
import { ConfirmDialog } from '../../components/DlpConfiguration/Common/ConfirmDialog';
import { useUnsavedChangesGuard } from '../../components/DlpConfiguration/Common/useUnsavedChangesGuard';
import { useActiveProject } from '../../contexts/ActiveProjectContext';
import '../../styles/custom/css/dlp-configuration.css';

type DlpTab = 'project' | 'scopes' | 'profiles';
const TABS: DlpTab[] = ['project', 'scopes', 'profiles'];
const NEW_SCOPE_ID = -1;

function DlpConfiguration() {
    const { project, status } = useActiveProject();
    const { t } = useTranslation();
    if (status === 'loading') return <p className="p-4" role="status">{t('dlpConfiguration.loading')}</p>;
    if (status === 'error') return <p className="p-4" role="alert">{t('dlpConfiguration.projectAccessError')}</p>;
    const code = project?.ProjectCode;
    if (typeof code !== 'string' || !code.trim()) {
        return <p className="p-4" role="alert">{t('dlpConfiguration.missingProjectCode')}</p>;
    }
    // Drop all editors/data immediately on project/code change, even before effects run.
    return <DlpConfigurationContent key={`${project?.Id}:${code}`} projectId={project!.Id} projectCode={code} />;
}

const selectionErrorKey = (error: unknown) => error instanceof DlpProjectSelectionError
    ? `dlpConfiguration.${error.reason}` : 'dlpConfiguration.loadError';

function DlpConfigurationContent({ projectId, projectCode }: { projectId: number, projectCode: string }) {
    const { t } = useTranslation();
    const dlpConfigurationService = new DlpConfigurationService(projectId);
    const serviceRef = useRef(dlpConfigurationService);
    serviceRef.current = dlpConfigurationService;
    const excelExportRef = useRef<ExcelExport>(null);

    const [config, setConfig] = useState<DlpConfigurationModel>();
    const [brands, setBrands] = useState<DlpLookupModel[]>([]);
    const [countries, setCountries] = useState<DlpLookupModel[]>([]);

    const [tab, setTab] = useState<DlpTab>('project');
    const [selectedScopeId, setSelectedScopeId] = useState<number>();
    const [selectedProfileName, setSelectedProfileName] = useState<string>();
    // A scope is saved as one row per brand, so a new one lives only in the page until its first save
    const [newScope, setNewScope] = useState<DlpScopeModel | null>(null);

    // Set by whichever editor is open, so tab/selection changes can ask before discarding
    const [isDirty, setIsDirty] = useState(false);
    const guard = useUnsavedChangesGuard(isDirty);

    const [loadError, setLoadError] = useState<string | null>(null);
    const requestVersion = useRef(0);
    const mounted = useRef(true);

    const reload = async () => {
        if (!mounted.current) return;
        const version = ++requestVersion.current;
        setConfig(undefined);
        setLoadError(null);
        try {
            const configuration = await dlpConfigurationService.GetConfiguration();
            if (mounted.current && version === requestVersion.current) setConfig(configuration);
        } catch (error) {
            if (mounted.current && version === requestVersion.current) setLoadError(selectionErrorKey(error));
        }
    };

    React.useEffect(() => {
        mounted.current = true;
        const version = ++requestVersion.current;
        const fetchData = async () => {
            try {
                const [configuration, brandList, countryList] = await Promise.all([
                    serviceRef.current.GetConfiguration(),
                    serviceRef.current.GetBrands(),
                    serviceRef.current.GetCountries()
                ]);
                if (!mounted.current || version !== requestVersion.current) return;
                setConfig(configuration);
                setBrands(brandList);
                setCountries(countryList);
            } catch (error) {
                if (mounted.current && version === requestVersion.current) setLoadError(selectionErrorKey(error));
            }
        };
        fetchData();
        return () => { mounted.current = false; requestVersion.current++; };
    }, [projectId, projectCode]);

    const allIssues = useMemo(
        () => config
            ? validateDlpConfiguration({ ...config, scopes: newScope ? [...config.scopes, newScope] : config.scopes }, id => brands.find(b => b.id === id)?.name ?? id)
            : [],
        [config, newScope, brands]
    );

    if (!config) {
        return <p className="p-4" role={loadError ? 'alert' : 'status'}>{t(loadError ?? 'dlpConfiguration.loading', { code: projectCode })}</p>;
    }

    // Admin resolves zero/one by its server-owned code and rejects duplicate matches.
    const [project] = config.projects;
    const projectScopes = [...config.scopes, ...(newScope ? [newScope] : [])].filter(s => s.project_id === project?.id);
    const projectScopeIds = projectScopes.map(s => s.scope_id);
    const projectScopeBrands = config.scopeBrands.filter(sb => projectScopeIds.includes(sb.scope_id));
    const projectProfiles = config.profiles.filter(p => p.project_id === project?.id);
    const projectIssues = allIssues.filter(i =>
        (i.target === 'projects' && i.targetId === project?.id) ||
        (i.target === 'scopes' && projectScopeIds.includes(i.targetId as number)) ||
        (i.target === 'profiles' && String(i.targetId).startsWith(`${project?.id}::`)));

    // --- Projects ---
    const saveProject = async (updated: DlpProjectModel) => {
        if (await dlpConfigurationService.UpdateProject(updated)) await reload();
    };

    // Leaving the current editor (after the discard check) also drops an unsaved new scope
    const navigate = (action: () => void) => guard.run(() => {
        setNewScope(null);
        action();
    });

    // --- Scopes ---
    const openScope = (scopeId: number) => navigate(() => {
        setTab('scopes');
        setSelectedScopeId(scopeId);
    });

    const createScope = async () => navigate(() => {
        setNewScope({
            scope_id: NEW_SCOPE_ID, project_id: project.id, name: `new_scope_${Date.now().toString(36).slice(-4)}`,
            all_brands: false, channels: null, countries: null, industries: null,
            quota_profile: '', start_from: new Date().toISOString().slice(0, 10), ended_at: null
        });
        setSelectedScopeId(NEW_SCOPE_ID);
    });

    const saveScope = async (scope: DlpScopeModel, brandIds: string[]) => {
        const saved = scope.scope_id === NEW_SCOPE_ID
            ? await dlpConfigurationService.CreateScope(scope, brandIds)
            : await dlpConfigurationService.UpdateScope(config.scopes.find(s => s.scope_id === scope.scope_id)?.name ?? scope.name, scope, brandIds);
        if (!saved) return;
        if (scope.scope_id === NEW_SCOPE_ID) setNewScope(null);
        await reload();
        // A scope's id is its first row's, which can change when its brands change
        setSelectedScopeId(saved.scope_id);
    };

    const deleteScope = async (scopeId: number) => {
        const saved = config.scopes.find(s => s.scope_id === scopeId);
        if (saved) {
            if (!await dlpConfigurationService.DeleteScope(project.id, saved.name)) return;
            await reload();
        }
        setNewScope(null);
        setSelectedScopeId(undefined);
    };

    // --- Quota profiles ---
    const createProfile = async (copyFrom?: string) => guard.run(async () => {
        const copy = copyFrom ? { name: copyFrom, rows: projectProfiles.filter(p => p.profile_name === copyFrom) } : undefined;
        const name = await dlpConfigurationService.CreateQuotaProfile(project.id, copy);
        if (!name) return;
        await reload();
        setSelectedProfileName(name);
    });

    const saveProfile = async (currentName: string, newName: string, rows: DlpQuotaProfileRowModel[]) => {
        if (!await dlpConfigurationService.UpdateQuotaProfile(project.id, currentName, newName, rows)) return;
        await reload();
        setSelectedProfileName(newName);
    };

    const deleteProfile = async (name: string) => {
        if (!await dlpConfigurationService.DeleteQuotaProfile(project.id, name)) return;
        await reload();
        setSelectedProfileName(undefined);
    };

    const exportExcel = () => {
        if (project) excelExportRef.current?.save(buildDlpConfigurationWorkbook(config, project.id));
    };

    return (
        <div className="dlp-configuration">
            {!project ? (
                <div className="content">
                    <div className="empty-note">{t('dlpConfiguration.project.noProjects', { code: projectCode })}</div>
                </div>
            ) : (
                <>
                    <div className="nav-row">
                        <div className="nav-tabs-left">
                            {TABS.map(key => (
                                <button type="button" key={key} aria-pressed={tab === key} className={`nav-tab ${tab === key ? 'active' : ''}`} onClick={() => navigate(() => setTab(key))}>
                                    {t(`dlpConfiguration.tabs.${key}`)}
                                    {key === 'scopes' && <span className="nav-count">{projectScopes.length}</span>}
                                </button>
                            ))}
                        </div>
                        <div className="nav-tabs-right">
                            <button type="button" className="lm-btn-secondary sm" onClick={exportExcel}>
                                <SvgIcon icon={downloadIcon} size="small" /> <Trans>general.exportToExcel</Trans>
                            </button>
                            <ExcelExport ref={excelExportRef} fileName="dlp_config_export.xlsx" />
                        </div>
                    </div>

                    <div className="content">
                        {tab === 'project' && (
                            <ProjectSettingsPanel
                                key={project.id}
                                project={project}
                                linkedScopeCount={projectScopes.length}
                                issues={projectIssues.filter(i => i.target === 'projects')}
                                onSave={saveProject}
                                onDirtyChange={setIsDirty}
                            />
                        )}
                        {tab === 'scopes' && (
                            <ScopesPanel
                                key={project.id}
                                scopes={projectScopes}
                                scopeBrands={projectScopeBrands}
                                profiles={projectProfiles}
                                brands={brands}
                                countries={countries}
                                issues={projectIssues.filter(i => i.target === 'scopes')}
                                selectedId={selectedScopeId}
                                onSelect={id => navigate(() => setSelectedScopeId(id))}
                                onCreate={createScope}
                                onSave={saveScope}
                                onDelete={deleteScope}
                                onDirtyChange={setIsDirty}
                            />
                        )}
                        {tab === 'profiles' && (
                            <ProfilesPanel
                                key={project.id}
                                project={project}
                                profiles={projectProfiles}
                                scopes={projectScopes}
                                issues={projectIssues.filter(i => i.target === 'profiles')}
                                selectedName={selectedProfileName}
                                onSelect={name => guard.run(() => setSelectedProfileName(name))}
                                onCreate={() => createProfile()}
                                onSave={saveProfile}
                                onDuplicate={createProfile}
                                onDelete={deleteProfile}
                                onOpenScope={openScope}
                                onDirtyChange={setIsDirty}
                            />
                        )}
                    </div>
                </>
            )}

            {guard.isPending && (
                <ConfirmDialog
                    title={t('dlpConfiguration.discardTitle')}
                    message={t('dlpConfiguration.discardMessage')}
                    confirmLabel={t('dlpConfiguration.discard')}
                    onConfirm={guard.confirm}
                    onCancel={guard.cancel}
                    danger
                />
            )}
        </div>
    );
}

export default DlpConfiguration;
